import { cva } from 'class-variance-authority';

/**
 * Tooltip class recipes — the pill, its detached dot, and the layer they float on.
 *
 * Zero `dark:` variants, as everywhere else in this layer: colour comes entirely from the
 * `--ds-tooltip-*` slots, which `.ds-tooltip` sets in `components/styles/design-system.css`. Read
 * the block header there first — the ICON's colour is the one place this port deliberately departs
 * from what Figma binds, and the measurement that forced it is recorded there.
 *
 * Geometry is read off the `Tooltip` component set (node 12:1033), from the bound variables rather
 * than off a render. Every number below is a token in the library:
 *
 *   pill padding   `x1,5` / `x4`      6px down, 16px across
 *   pill radius    `radius/full`      a capsule at any height
 *   icon-to-label  `x1,5`             6px
 *   dot size       `x2`               8px
 *   pill-to-dot    `x1,5`             6px
 *   label          `Label/md`         12px Medium on a 20px line
 *
 * Which makes the pill 32px tall with a single-line label, which is what the set draws.
 */

/**
 * The positioner.
 *
 * Only a stacking context — all the geometry is inline styles from Base UI's anchor positioning.
 * The z-index belongs HERE rather than on the popup, because this is the element Base UI gives
 * `position: fixed`; on the popup it would have nothing to stack against.
 *
 * `z-[1500]` is MUI's tooltip layer, and this is the one popup in this layer that does NOT take
 * `z-[1400]`. `Menu`, `Select` and `DatePicker` all sit at 1400 — above MUI's modal (1300) and
 * drawer (1200), below its tooltip — and the comment beside each of them names 1500 as the ceiling
 * they stay under. This component is what lives there.
 *
 * It has to be above them rather than beside them: a tooltip on a control INSIDE an open menu or a
 * date picker is an ordinary case, and a description rendered behind the surface that owns the
 * control is worse than no description. Same reasoning as the siblings', one step up the same
 * scale — the value is MUI's because MUI is what it coexists with during the refresh.
 */
export const tooltipPositionerRecipe = cva(['z-[1500]']);

/**
 * The pill.
 *
 * `ds-tooltip` is applied by the component rather than inherited, for the reason `Menu` and
 * `Select` document: the popup is portalled into `<body>`, so it has none of the trigger's
 * ancestors and would otherwise resolve every slot to its light-mode fallback. The theme class goes
 * on the `Portal` instead, which IS an ancestor of this element.
 *
 * ── It is the only surface in this layer that INVERTS ────────────────────────────────────────────
 *
 * `--ds-tooltip-surface` is `background/inverted`, so the pill is near-black on a light page and
 * near-white on a dark one — the opposite way round to every panel, menu and field in the system.
 * Two consequences worth knowing before editing anything here:
 *
 *   - there is NO border. Every other floating surface in DS 2.0 carries a 1px edge, because
 *     elevation was removed and the border is what separates a surface from what is behind it. An
 *     inverted fill does that job on its own: measured 21:1 against the light page and 17.6:1
 *     against the dark secondary surface (2026-09-17). A border here would be decoration.
 *   - the foreground slots are the `*-inverted` roles, not the ordinary ones. Naming
 *     `--foreground-primary` on this surface would draw near-black text on near-black.
 *
 * ── `w-max` with a cap, and the cap is not in the drawing ───────────────────────────────────────
 *
 * The set draws one 128px cell holding the placeholder string, so it specifies no maximum width and
 * no wrapping behaviour. Left uncapped, `w-max` means a long label produces a single pill as wide as
 * the sentence — a 70-character description is roughly 450px of unbroken capsule.
 *
 * `max-w-[280px]` with wrapping is `menuItemDescriptionRecipe`'s answer to the same problem, and the
 * number is borrowed from it rather than re-derived. Wrapping rather than truncating, because this
 * element IS the text: truncating a tooltip hides the only thing it exists to say. A wrapped tooltip
 * keeps `rounded-full`, so it reads as a taller capsule — acceptable, and the set's own description
 * says the label is short. Recorded as a gap; a multi-line cell would settle both the cap and the
 * radius.
 *
 * `pointer-events-none`: the pill is not a click target. It has no interactive content and it floats
 * 20px from its control over content it knows nothing about, so a click aimed at whatever is
 * underneath it should land there rather than on a label.
 *
 * ── AND IT DOES NOT BREAK WCAG 1.4.13, WHICH IS THE NON-OBVIOUS PART ───────────────────────────
 *
 * 1.4.13 "Hoverable" requires that the pointer can be moved over the additional content without it
 * disappearing, and the obvious reading is that a pill which cannot receive pointer events fails it:
 * the pointer leaves the trigger, the trigger's `:hover` ends, the tooltip closes.
 *
 * Measured 2026-09-18 in Chromium, and it does not happen. Base UI does not keep the tooltip open
 * off the POPUP's hover — it tracks the pointer against a safe region spanning the trigger, the gap
 * and the popup, so moving onto the pill keeps it open while `pointer-events` computes to `none`.
 * Both halves were read from the same run: still open with the pointer on the pill, and
 * `pointerEvents: "none"` on that same element.
 *
 * So do not remove this class to "fix" 1.4.13. The behaviour it looks like it breaks is not
 * implemented the way it looks. `HoverAndDismiss` pins the dismissal half, and this paragraph is
 * dated because it is a claim about a dependency's internals rather than about our own CSS.
 *
 * `relative` and `group/tooltip` both exist for the DOT, which is an absolutely-positioned child
 * reading this element's resolved `data-side` and `data-align` — see `tooltipDotRecipe`.
 *
 * ── The motion ──────────────────────────────────────────────────────────────────────────────────
 *
 * Opacity and scale only, which is the one rule worth not negotiating: both skip layout and paint
 * and run on the compositor, so the pill cannot drop frames under load. `Accordion`'s panel is the
 * only primitive in this layer animating anything else, and it animates a height it had to have
 * Base UI measure first.
 *
 * The tooltip is an anchored popup, so it reads the overlay entrance and exit roles that Menu,
 * Select and DatePicker use. The exit is shorter than the entrance because it acknowledges a
 * decision already made; both use the system's entrance curve so neither begins with visible lag.
 *
 * `data-starting-style` and `data-ending-style` are Base UI's two transition hooks, as
 * `accordionPanelRecipe` documents: the first is present for one frame as the pill mounts, the second
 * for the duration of the close. The close hook is also what keeps the pill mounted long enough to
 * animate out — Base UI defers the unmount until the animations on this element have finished.
 *
 * The transform origin is read from the positioner's measured anchor. That makes the pill grow out
 * of the control it belongs to rather than swelling from its own middle — and because the dot is a
 * CHILD, it scales and fades on exactly the same curve. That is the paired-elements rule satisfied
 * structurally rather than by keeping two sets of numbers in step.
 *
 * Base UI's instant state handles the case the transition gets wrong. Moving between two adjacent
 * triggers inside the delay window is the common one: animating there reads as the label lagging
 * behind the cursor. That state only exists inside a `TooltipProvider` — outside one, Base UI never
 * sets `data-instant` and every neighbour waits out its own delay and plays its own entrance.
 *
 * ── Reduced motion ─────────────────────────────────────────────────────────────────────────────
 *
 * Reduced motion keeps the fade, because opacity communicates appearance and disappearance, and
 * opts the lifecycle scale in only when motion is safe. The state-qualified scale cannot be
 * suppressed afterwards: its selector is more specific than a bare reduced-motion override.
 */
export const tooltipPopupRecipe = cva([
  'ds-tooltip group/tooltip',
  'pointer-events-none relative box-border flex w-max max-w-[280px] items-center gap-[6px]',
  'px-[16px] py-[6px]',
  'rounded-full',
  'bg-[var(--ds-tooltip-surface)]',
  'font-sans text-label-md text-[var(--ds-tooltip-fg)]',
  'outline-none',
  '[transform-origin:var(--transform-origin,center)]',
  'transition-[opacity,scale] duration-[var(--motion-overlay)] ease-[var(--motion-overlay-ease)]',
  'data-[ending-style]:duration-[var(--motion-overlay-exit)]',
  'data-[ending-style]:ease-[var(--motion-overlay-exit-ease)]',
  'data-[starting-style]:opacity-0 data-[ending-style]:opacity-0',
  'motion-safe:data-[starting-style]:scale-[0.96] motion-safe:data-[ending-style]:scale-[0.96]',
  'data-[instant]:transition-none',
]);

/**
 * The leading glyph, 16px.
 *
 * TWO sizing rules, because there are two kinds of glyph and neither rule reaches both — the split
 * `menuItemIconRecipe` and `buttonIconRecipe` both carry, in this component's copy of it:
 *
 *   - `[&_svg]:size-full` sizes a RAW svg, which has no font-size relationship to inherit.
 *   - `text-[length:16px]` sizes a MUI-backed glyph, i.e. anything out of `icons/`. `SvgIcon` draws
 *     itself at `1em`, so giving the SLOT a font-size is what makes that `1em` resolve to 16px.
 *     `size-full` cannot do it: it is a Tailwind utility in `@layer utilities`, while `SvgIcon`'s
 *     `1em` is UNLAYERED Emotion, and unlayered beats layered whatever the specificity.
 *
 * The colour is a NAMED slot rather than inherited `currentColor`, and that is deliberate even
 * though `--ds-tooltip-icon` resolves to the same value as `--ds-tooltip-fg` today. The set binds a
 * different role to the glyph than to the label, so the two are separate decisions in the design
 * even where they currently agree — and the role the set picks is one this port does not reproduce.
 * See the `.ds-tooltip` block for the measurement; the short version is that
 * `foreground/secondary/default` on this surface measures 2.51:1 in light and 2.15:1 in dark.
 */
export const tooltipIconRecipe = cva([
  'inline-flex size-[16px] shrink-0 items-center justify-center',
  'text-[length:16px] text-[var(--ds-tooltip-icon)]',
  '[&_svg]:size-full',
]);

/**
 * The dot — the tooltip's pointer, and the part of this component that surprises people.
 *
 * It is a CIRCLE, 8px, DETACHED from the pill by 6px, filled with the pill's own surface. Not a
 * triangle, not attached, not an arrow: the set draws an `ELLIPSE` named `Dot` in all eight cells,
 * standing 6px clear of the capsule. Both numbers are tokens (`x2` and `x1,5`).
 *
 * ── It is pinned to the PILL, not to the trigger, and that was a real defect ────────────────────
 *
 * Measured off the set on 2026-09-18, as the dot's centre relative to the pill's centre:
 *
 *   Top / Bottom / Left / Right          0        centred on the pill's cross axis
 *   TopLeft / BottomLeft                +60       dot's RIGHT edge level with the pill's
 *   TopRight / BottomRight              -60       dot's LEFT edge level with the pill's
 *
 * So the file's rule is "centred, or flush to the near corner", measured against the pill. Every
 * number is the pill's own geometry; no cell positions the dot against anything else.
 *
 * This first shipped on Base UI's `Tooltip.Arrow`, which tracks the TRIGGER's centre instead and
 * writes an inline `left`/`top` that no class can override. Measured in the browser on the same
 * date, that put the dot 44px inside the corner on all four corner cells — and because the pill is
 * aligned to the trigger anyway, `dotCentre - triggerCentre` came back 0 or 1 on every one of the
 * eight. Which is the actual bug: `align="end"` and `align="center"` rendered nearly identically
 * whenever the trigger was a similar width to the pill, so four of the eight drawn cells were
 * unreachable. The PR that shipped it argued this "beats the drawing"; the measurement says it
 * erases half of it.
 *
 * So the `Arrow` is gone and this is an ordinary absolutely-positioned child of the pill.
 *
 * ── Keyed on the RESOLVED side and align, not on the props ──────────────────────────────────────
 *
 * `group-[…]/tooltip` reads `data-side` and `data-align` off the popup, which Base UI sets to the
 * placement it actually used. The obvious alternative — cva variants fed from `side` and `align` —
 * is shorter and typed, and it is wrong: collision avoidance can FLIP a `top` tooltip to `bottom`
 * when there is no room above the trigger, and a prop only ever describes what the caller asked
 * for. A dot on the wrong side of a flipped pill points away from the control.
 *
 * That is why the sixteen cross-axis classes are written out one per cell rather than composed from
 * constants: an interpolated class name is invisible to Tailwind's scanner, so it would generate no
 * CSS at all and the dot would sit wherever `absolute` put it.
 *
 * The main axis is the 6px gap: `top-full` plus `mt-[6px]` puts the dot's near edge 6px past the
 * pill's edge, on whichever side it points from.
 *
 * It inherits the pill's `pointer-events-none`, and it scales and fades with the pill because it is
 * a child — see the motion note on `tooltipPopupRecipe`.
 */
export const tooltipDotRecipe = cva([
  'absolute size-[8px] rounded-full bg-[var(--ds-tooltip-surface)]',

  // MAIN AXIS — 6px clear of the edge it points from.
  'group-data-[side=top]/tooltip:top-full group-data-[side=top]/tooltip:mt-[6px]',
  'group-data-[side=bottom]/tooltip:bottom-full group-data-[side=bottom]/tooltip:mb-[6px]',
  'group-data-[side=left]/tooltip:left-full group-data-[side=left]/tooltip:ml-[6px]',
  'group-data-[side=right]/tooltip:right-full group-data-[side=right]/tooltip:mr-[6px]',

  // CROSS AXIS, horizontal on a vertical side. `end` is Figma's `TopLeft`/`BottomLeft` — the pill
  // sits left of the trigger, so the dot goes to its right edge. `start` is the mirror.
  'group-[[data-side=top][data-align=center]]/tooltip:left-1/2',
  'group-[[data-side=top][data-align=center]]/tooltip:-translate-x-1/2',
  'group-[[data-side=top][data-align=start]]/tooltip:left-0',
  'group-[[data-side=top][data-align=end]]/tooltip:right-0',
  'group-[[data-side=bottom][data-align=center]]/tooltip:left-1/2',
  'group-[[data-side=bottom][data-align=center]]/tooltip:-translate-x-1/2',
  'group-[[data-side=bottom][data-align=start]]/tooltip:left-0',
  'group-[[data-side=bottom][data-align=end]]/tooltip:right-0',

  // CROSS AXIS, vertical on a horizontal side. The set draws only the centred cell here; `start`
  // and `end` follow the rule above rather than a drawing, and are flagged as extrapolated.
  'group-[[data-side=left][data-align=center]]/tooltip:top-1/2',
  'group-[[data-side=left][data-align=center]]/tooltip:-translate-y-1/2',
  'group-[[data-side=left][data-align=start]]/tooltip:top-0',
  'group-[[data-side=left][data-align=end]]/tooltip:bottom-0',
  'group-[[data-side=right][data-align=center]]/tooltip:top-1/2',
  'group-[[data-side=right][data-align=center]]/tooltip:-translate-y-1/2',
  'group-[[data-side=right][data-align=start]]/tooltip:top-0',
  'group-[[data-side=right][data-align=end]]/tooltip:bottom-0',
]);
